import { Headline } from '@almbrand/headline';
import { Modal } from '@almbrand/modal';
import { RichText } from '@almbrand/richtext';
import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setModal } from 'store/slices/modalSlice';
import styles from './ModalComponent.module.scss';

export const ModalComponent = () => {
	const dispatch = useAppDispatch();
	const { displayModal, modalProps, previousFocusElement } = useAppSelector((state) => state.modal);
	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (displayModal) {
			// Set focus on modal
			if (modalRef.current) {
				modalRef.current.focus();
			}
		} else if (previousFocusElement) {
			// Restore focus when modal closes using the stored selector
			const elementToFocus = document.querySelector(previousFocusElement);
			if (elementToFocus instanceof HTMLElement) {
				elementToFocus.focus();
				// Clean up the data attribute if it was added
				if (previousFocusElement.startsWith('[data-focus-restore-')) {
					const attributeStart = previousFocusElement.indexOf('[');
					const attributeEnd = previousFocusElement.indexOf('=');

					if (attributeStart !== -1 && attributeEnd > attributeStart + 1) {
						const attributeName = previousFocusElement.slice(attributeStart + 1, attributeEnd);
						elementToFocus.removeAttribute(attributeName);
					}
				}
			}
		}
	}, [displayModal, previousFocusElement]);

	if (!displayModal || !modalProps) return null;

	const handleClose = () => dispatch(setModal({ displayModal: false }));

	const mobileVariation = (): 'default' | 'fullWidth' | 'fullHeight' | 'fullHeightFullWidth' | 'dialog' => {
		for (const field of modalProps.fields ?? []) {
			// for now only support select fields, but we can add more conditions for other field types if needed
			if (field?.props?.contentType === 'SelectFieldBlock') {
				return 'fullHeightFullWidth';
			}
		}
		return 'fullWidth';
	};

	return (
		<Modal
			mode='default'
			mobileVariation={mobileVariation()}
			{...modalProps}
			handleClose={handleClose}
		>
			{
				<div
					ref={modalRef}
					className={styles.ModalComponent}
					tabIndex={-1}
				>
					{modalProps.subtitle && (
						<div className={styles.ModalComponent__headline}>
							<Headline
								fontColor='primary'
								size='default'
								title={modalProps.subtitle}
								variant='h2'
							/>
						</div>
					)}
					<RichText
						description={modalProps.content}
						className={
							(modalProps.className, modalProps.isErrorModal ? styles.ModalComponent__error : undefined)
						}
					/>
					{modalProps.fields?.map((field, idx) => <div key={idx + '_'}>{field}</div>)}
				</div>
			}
		</Modal>
	);
};
